import {AppError} from '../util/errors'

/**
 * Injects the 8th Wall engine script at runtime rather than declaring it in
 * `index.html`.
 *
 * Why: the engine plus its SLAM chunk is ~6.5 MB. From P2 onward a pre-flight
 * gate rejects unsupported and in-app browsers, and those users must not pay
 * that download before being told to open Safari or Chrome. Keeping the tag out
 * of the HTML is what makes the gate worth anything.
 *
 * The engine derives the base URL for its chunks and resources from
 * `new URL('./', document.currentScript.src)` - there is no override attribute
 * in 1.0.0. Per the HTML spec `document.currentScript` is set for classic
 * scripts regardless of how they were inserted, so dynamic injection is safe,
 * but `assertEngineBaseUrlResolved` below turns a spec violation into a clear
 * error instead of a silent 404 on `xr-slam.js`.
 */

const ENGINE_SRC = '/external/xr/xr.js'

/**
 * Only the SLAM chunk is preloaded. `xr-face.js` is 7.7 MB and unused by this
 * experience; leaving it out of the preload list keeps it off the wire.
 */
const PRELOAD_CHUNKS = 'slam'

const SLAM_CHUNK_SRC = '/external/xr/xr-slam.js'

export interface EngineLoadOptions {
  readonly timeoutMs?: number
}

/**
 * Hints the browser to start fetching the SLAM chunk immediately.
 *
 * The engine loads it via dynamic import and awaits it *before* setting
 * `window.XR8`, so it is squarely on the critical path. Warming it in parallel
 * with `xr.js`'s own download and parse saves a full round trip.
 */
const preloadSlamChunk = (): void => {
  const link = document.createElement('link')
  link.rel = 'modulepreload'
  link.href = SLAM_CHUNK_SRC
  document.head.append(link)
}

export const loadEngineScript = (options: EngineLoadOptions = {}): Promise<void> => {
  const timeoutMs = options.timeoutMs ?? 20_000

  if (document.querySelector(`script[src="${ENGINE_SRC}"]`)) return Promise.resolve()

  preloadSlamChunk()

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = ENGINE_SRC
    script.async = true
    script.dataset['preloadChunks'] = PRELOAD_CHUNKS

    const timer = window.setTimeout(() => {
      reject(new AppError('ENGINE_LOAD_FAILED', `${ENGINE_SRC} did not load within ${timeoutMs}ms`))
    }, timeoutMs)

    script.addEventListener(
      'load',
      () => {
        window.clearTimeout(timer)
        resolve()
      },
      {once: true}
    )
    script.addEventListener(
      'error',
      () => {
        window.clearTimeout(timer)
        reject(new AppError('ENGINE_LOAD_FAILED', `failed to fetch ${ENGINE_SRC}`))
      },
      {once: true}
    )

    document.head.append(script)
  })
}
